import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Heart, MessageCircle, Send, ImagePlus, Loader2, Sparkles } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { motion } from "framer-motion";
import { toast } from "sonner";

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

function PostCard({ post, onLike, onComment, nav }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const a = post.author || {};
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden" data-testid="post-card">
      <div className="flex items-center gap-3 p-4">
        <Avatar className="h-11 w-11 cursor-pointer ring-2 ring-pink-100" onClick={() => nav(`/profile/${a.id}`)}>
          <AvatarImage src={a.avatar} /><AvatarFallback className="bg-fuchsia-100 text-fuchsia-700 font-bold">{(a.name||"U")[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-bold text-slate-900 truncate cursor-pointer hover:text-pink-600" onClick={() => nav(`/profile/${a.id}`)}>{a.name}</p>
            {a.role === "brand" && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold uppercase">Brand</span>}
          </div>
          <p className="text-xs text-slate-400">{a.category || a.company} · {timeAgo(post.created_at)}</p>
        </div>
      </div>
      <p className="px-4 pb-3 text-slate-700 leading-relaxed">{post.content}</p>
      {post.image && <img src={post.image} alt="post" className="w-full max-h-[480px] object-cover" />}
      <div className="flex items-center gap-5 px-4 py-3">
        <button data-testid={`like-btn-${post.id}`} onClick={() => onLike(post.id)} className="flex items-center gap-1.5 group">
          <Heart className={`h-5 w-5 transition-all group-active:scale-125 ${post.liked ? "fill-rose-500 text-rose-500" : "text-slate-500 group-hover:text-rose-500"}`} />
          <span className={`text-sm font-semibold ${post.liked ? "text-rose-500" : "text-slate-500"}`}>{post.like_count}</span>
        </button>
        <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-slate-500 hover:text-pink-600">
          <MessageCircle className="h-5 w-5" /><span className="text-sm font-semibold">{post.comments.length}</span>
        </button>
      </div>
      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
          {post.comments.map((c) => (
            <div key={c.id} className="flex gap-2.5 text-sm">
              <span className="font-bold text-slate-800">{c.name}</span>
              <span className="text-slate-600">{c.text}</span>
            </div>
          ))}
          <div className="flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…" data-testid={`comment-input-${post.id}`}
              className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2 text-sm focus:bg-white focus:ring-2 focus:ring-pink-500/20 outline-none" />
            <button data-testid={`comment-send-${post.id}`} onClick={() => { if (text.trim()) { onComment(post.id, text); setText(""); } }}
              className="h-9 w-9 rounded-full brand-gradient flex items-center justify-center text-white"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/posts");
    setPosts(data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const createPost = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post("/posts", { content, image });
      setPosts([data, ...posts]); setContent(""); setImage("");
      toast.success("Posted!");
    } finally { setPosting(false); }
  };

  const like = async (id) => {
    const { data } = await api.post(`/posts/${id}/like`);
    setPosts((p) => p.map((x) => (x.id === id ? data : x)));
  };
  const comment = async (id, text) => {
    const { data } = await api.post(`/posts/${id}/comment`, { text });
    setPosts((p) => p.map((x) => (x.id === id ? data : x)));
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-5 mb-6">
        <div className="flex gap-3">
          <Avatar className="h-11 w-11 ring-2 ring-pink-100"><AvatarImage src={user?.avatar} /><AvatarFallback className="bg-fuchsia-100 text-fuchsia-700 font-bold">{(user?.name||"U")[0]}</AvatarFallback></Avatar>
          <textarea data-testid="post-content" value={content} onChange={(e) => setContent(e.target.value)} rows={2} placeholder="Share an update, campaign or collab…"
            className="flex-1 resize-none bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none transition-all" />
        </div>
        {image && <img src={image} alt="preview" className="mt-3 rounded-xl max-h-60 w-full object-cover" />}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2 flex-1 mr-3">
            <ImagePlus className="h-5 w-5 text-pink-500 shrink-0" />
            <input data-testid="post-image" value={image} onChange={(e) => setImage(e.target.value)} placeholder="Paste image URL (optional)"
              className="flex-1 text-sm bg-transparent outline-none text-slate-600 placeholder:text-slate-400" />
          </div>
          <button data-testid="post-submit" onClick={createPost} disabled={posting || !content.trim()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-white brand-gradient shadow-md shadow-pink-500/30 hover:-translate-y-0.5 transition-all disabled:opacity-50">
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Post
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-pink-500" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Sparkles className="h-10 w-10 mx-auto mb-3 text-pink-300" />
          <p className="font-semibold">No posts yet. Be the first to share!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {posts.map((p) => <PostCard key={p.id} post={p} onLike={like} onComment={comment} nav={nav} />)}
        </div>
      )}
    </div>
  );
}
